import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { Loader2, Clock, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

const STATUS_DETAIL = {
  submitted: { i: <Clock />, c: "text-amber-300", label: "Submitted", msg: "Mickey will review your application shortly." },
  under_review: { i: <Clock />, c: "text-amber-300", label: "Under Review", msg: "We're looking it over." },
  need_info: { i: <AlertCircle />, c: "text-orange-300", label: "Need More Info", msg: "Mickey has reached out with a question." },
  approved: { i: <CheckCircle2 />, c: "text-emerald-300", label: "Approved", msg: "Welcome to the host program! Check your email for next steps." },
  offer_sent: { i: <CheckCircle2 />, c: "text-emerald-300", label: "Offer Sent", msg: "Your custom offer is in your dashboard." },
  declined: { i: <XCircle />, c: "text-red-300", label: "Declined", msg: "Unfortunately we couldn't move forward this time." },
};

export default function ApplicationStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    http.get(`/partner/application-status/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Application not found."));
  }, [token]);

  return (
    <Layout>
      <section className="py-24 px-6 min-h-[60vh]">
        <div className="max-w-xl mx-auto border border-white/10 bg-[#0a0a0a] p-10">
          {err ? (
            <p className="text-center text-red-300">{err}</p>
          ) : !data ? (
            <p className="text-center text-[#8a857d]"><Loader2 className="animate-spin inline-block mr-2" size={16} />Loading…</p>
          ) : (
            <div data-testid="status-card">
              <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">Application Status</div>
              <h1 className="font-display text-3xl text-[#F4F1EA]">{data.business_name}</h1>
              <p className="font-body text-[12px] text-[#8a857d] mt-1">Submitted {new Date(data.created_at).toLocaleDateString()}</p>

              {(() => {
                const det = STATUS_DETAIL[data.status] || { i: <Clock />, c: "text-zinc-300", label: data.status, msg: "" };
                return (
                  <div className="mt-6 flex items-start gap-4">
                    <div className={`${det.c} mt-1`}>{det.i}</div>
                    <div>
                      <div className="font-ui text-sm tracking-[0.18em] uppercase text-[#F4F1EA]">{det.label}</div>
                      <p className="font-body text-[14px] text-[#B5B0A7] mt-1">{det.msg}</p>
                    </div>
                  </div>
                );
              })()}

              {data.history?.length > 0 && (
                <div className="mt-8">
                  <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-2">Timeline</div>
                  <ol className="border-l border-[#C9A055]/30 pl-5 space-y-3">
                    {data.history.map((h, i) => (
                      <li key={i} className="relative">
                        <span className="absolute -left-[27px] top-1 w-2 h-2 rounded-full bg-[#C9A055]" />
                        <div className="font-ui text-[10px] tracking-[0.18em] uppercase text-[#C9A055]">{h.event}</div>
                        <div className="text-[12px] text-[#8a857d]">{new Date(h.at).toLocaleString()}</div>
                        {h.message && <div className="text-[13px] text-[#E8E4DC] mt-1">{h.message}</div>}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
